import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { CaseFileProvider, useCaseFile } from './contexts/CaseFileContext';
import { useMFAEnrollmentCheck } from './hooks/useMFAEnrollmentCheck';
import { Login, Dashboard, CreateCaseFile, CaseFileDetail, CaseFilePage, Documents, Onboarding, CareLogsPage, WatchtowerPage, CaseMakerPage, GeneratorWizardPage, FormFillerWizardPage, DocumentEditorPage, Profile, Settings, SearchResults, PatientAuthorization, PrivacyPolicy, Help, RequestAccess, BetaFeedbackPage, MFAEnrollment, EmailVerification, CalendarExportPlayground } from './pages';
import { MFAChallenge } from './components/auth/MFAChallenge';
import { SkipLink } from './components/ui/SkipLink';
import { CareRecipientProfile } from './pages/settings/CareRecipientProfile';
import { AccountSecurity } from './pages/settings/AccountSecurity';
import { DataPrivacy } from './pages/settings/DataPrivacy';
import { NotificationSettings } from './pages/settings/NotificationSettings';
import { AdminDashboard, FeedbackPage, AIActivityPage, WaitlistPage, UsersPage, BackfillSearchIndexPage } from './features/admin';

interface LocationState {
  caseFileId?: string;
  message?: string;
}

/**
 * Inner component that uses CaseFile context to determine routing
 */
function AuthenticatedRoutes(): JSX.Element {
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const { caseFiles, loading: caseFilesLoading, setActiveCaseFileId } = useCaseFile();
  const hasCheckedRef = useRef(false);

  // Handle caseFileId from location state (e.g., after creating a new CaseFile)
  useEffect(() => {
    if (locationState?.caseFileId && !hasCheckedRef.current) {
      setActiveCaseFileId(locationState.caseFileId);
      hasCheckedRef.current = true;
    }
  }, [locationState?.caseFileId, setActiveCaseFileId]);

  if (caseFilesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-airy">
        <div className="text-center">
          <div className="text-lg text-muted mb-2">Setting up your account...</div>
          <div className="text-sm text-muted/70">Just a moment</div>
        </div>
      </div>
    );
  }

  // User has no CaseFiles - show onboarding (but allow authorization and privacy pages)
  if (caseFiles.length === 0) {
    return (
      <Routes>
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/authorization" element={<PatientAuthorization />} />
        <Route path="/authorization/:authorizationId" element={<PatientAuthorization />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="*" element={<Navigate to="/onboarding" replace />} />
      </Routes>
    );
  }

  // User has CaseFiles - show normal app
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/search" element={<SearchResults />} />
      <Route path="/profile" element={<Profile />} />
      {/* New Settings Routes with sidebar layout */}
      <Route path="/settings" element={<CareRecipientProfile />} />
      <Route path="/settings/security" element={<AccountSecurity />} />
      <Route path="/settings/privacy" element={<DataPrivacy />} />
      <Route path="/settings/notifications" element={<NotificationSettings />} />
      {/* Legacy settings route - redirect to new settings */}
      <Route path="/settings-old" element={<Settings />} />
      <Route path="/create-casefile" element={<CreateCaseFile />} />
      <Route path="/casefile/:caseFileId" element={<CaseFilePage />} />
      <Route path="/casefile/:caseFileId/edit" element={<CaseFileDetail />} />
      <Route path="/casefile/:caseFileId/documents" element={<Documents />} />
      <Route path="/casefile/:caseFileId/carelogs" element={<CareLogsPage />} />
      <Route path="/casefile/:caseFileId/deadlines" element={<WatchtowerPage />} />
      <Route path="/casefile/:caseFileId/casemaker" element={<CaseMakerPage />} />
      <Route path="/casefile/:caseFileId/casemaker/new" element={<GeneratorWizardPage />} />
      <Route path="/casefile/:caseFileId/casemaker/form-filler" element={<FormFillerWizardPage />} />
      <Route path="/casefile/:caseFileId/casemaker/:docId" element={<DocumentEditorPage />} />
      <Route path="/authorization" element={<PatientAuthorization />} />
      <Route path="/authorization/:authorizationId" element={<PatientAuthorization />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/help" element={<Help />} />
      <Route path="/feedback" element={<BetaFeedbackPage />} />
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/feedback" element={<FeedbackPage />} />
      <Route path="/admin/ai-activity" element={<AIActivityPage />} />
      <Route path="/admin/waitlist" element={<WaitlistPage />} />
      <Route path="/admin/users" element={<UsersPage />} />
      <Route path="/admin/backfill" element={<BackfillSearchIndexPage />} />
      <Route path="/onboarding" element={<Navigate to="/" replace />} />
      {/* Dev playground routes — TASK-015. Not part of the production user surface. */}
      <Route path="/dev/calendar-export-playground" element={<CalendarExportPlayground />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Email verification guard — requires verified email before proceeding.
 * Sits between authentication and MFA enrollment in the guard chain:
 *   Login → EmailVerificationGuard → MFAGuard → App
 */
function EmailVerificationGuard({ children }: { children: React.ReactNode }): JSX.Element {
  const { user } = useAuth();

  if (user && !user.emailVerified) {
    return <EmailVerification />;
  }

  return <>{children}</>;
}

/**
 * MFA enforcement guard — requires MFA enrollment before accessing the app.
 * Users who haven't enrolled are shown the MFA enrollment page.
 */
function MFAGuard({ children }: { children: React.ReactNode }): JSX.Element {
  const { loading: mfaLoading, isEnrolled } = useMFAEnrollmentCheck();

  if (mfaLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-airy">
        <div className="text-lg text-muted">Checking security settings...</div>
      </div>
    );
  }

  if (!isEnrolled) {
    return <MFAEnrollment />;
  }

  return <>{children}</>;
}

/**
 * Wrapper component that provides CaseFile context
 */
function AuthenticatedApp(): JSX.Element {
  return (
    <EmailVerificationGuard>
      <MFAGuard>
        <CaseFileProvider>
          <AuthenticatedRoutes />
        </CaseFileProvider>
      </MFAGuard>
    </EmailVerificationGuard>
  );
}

function App(): JSX.Element {
  const { user, loading, mfaRequired } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-airy">
        <div className="text-lg text-muted">Loading...</div>
      </div>
    );
  }

  // Show MFA challenge screen during sign-in when user has MFA enrolled
  if (mfaRequired) {
    return <MFAChallenge />;
  }

  return (
    <BrowserRouter>
      <SkipLink />
      {user ? (
        <AuthenticatedApp />
      ) : (
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/request-access" element={<RequestAccess />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </BrowserRouter>
  );
}

export default App;
